var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useSWR from "swr";
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, } from "@tanstack/react-table";
import { Auth0Fetcher } from "../Auth0Fetcher";
import { useNavigate } from "react-router-dom";
import { HelpTooltip } from "./HelpTooltip";
// TUnused is left here because Typescript seems to blow up if there isn't a second template parameter. No idea why...
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export var TableBase = function (props) {
    var table = props.table, columnDefinitions = props.columnDefinitions, onClickRow = props.onClickRow, canClickRow = props.canClickRow, buildRowLink = props.buildRowLink, paginated = props.paginated, loading = props.loading, emptyMessage = props.emptyMessage;
    var navigate = useNavigate();
    var hasRows = table && table.getRowModel() && table.getRowModel().rows.length > 0;
    var maxFooterPages = 5;
    var canClickRowSimplified = useCallback(function (row) {
        return ((onClickRow || buildRowLink) &&
            (canClickRow == undefined || canClickRow(row)));
    }, [buildRowLink, canClickRow, onClickRow]);
    var onClickRowSimplified = useCallback(function (event, row) {
        if (canClickRowSimplified(row)) {
            if (onClickRow) {
                onClickRow(row);
                return;
            }
            else if (buildRowLink === null || buildRowLink === void 0 ? void 0 : buildRowLink(row)) {
                var rowLink = buildRowLink(row);
                // Since row level wrapping isn't supported:
                // https://github.com/TanStack/table/discussions/2336#discussioncomment-620512
                // Either ctrl/meta + click or middle click.
                if (event.metaKey || event.ctrlKey || event.button === 1) {
                    window.open(rowLink, "_blank");
                }
                else {
                    navigate(rowLink);
                }
            }
        }
    }, [buildRowLink, canClickRowSimplified, onClickRow]);
    return (_jsxs(_Fragment, { children: [_jsxs("table", __assign({ className: "table-auto w-full max-w-[1150px]" }, { children: [!hasRows && (_jsx("tr", __assign({ className: "flex flex-row w-full items-center justify-center font-N7 text-white h-[250px]" }, { children: _jsx("td", __assign({ className: "w-full text-center" }, { children: "".concat(loading
                                ? "Loading..."
                                : emptyMessage !== null && emptyMessage !== void 0 ? emptyMessage : "No data for the selected time range. Please select a different time range.") })) }))), hasRows && (_jsxs(_Fragment, { children: [_jsx("thead", __assign({ className: "" }, { children: table.getHeaderGroups().map(function (headerGroup) { return (_jsx("tr", { children: headerGroup.headers.map(function (header, idx) { return (_jsx("th", __assign({ className: "px-[32px] py-[16px] text-left font-N8 text-black-600 ".concat(columnDefinitions[idx].wrapHeader
                                            ? "whitespace-normal"
                                            : "whitespace-nowrap"), style: { width: columnDefinitions[idx].size } }, { children: _jsxs("div", __assign({ className: "flex flex-row gap-x-2 items-center" }, { children: [header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext()), columnDefinitions[idx].helpText != null && (_jsx(HelpTooltip, { text: columnDefinitions[idx].helpText }))] })) }), header.id)); }) }, headerGroup.id)); }) })), _jsx("tbody", { children: table.getRowModel().rows.map(function (row, idx) { return (_jsx("tr", __assign({ className: "".concat(idx % 2 == 0 ? "bg-black-1000" : "bg-black-1200", " ").concat(canClickRowSimplified(row.original) ? "cursor-pointer" : "", " ").concat(canClickRowSimplified(row.original)
                                        ? "hover:bg-black-800"
                                        : ""), onClick: function (event) {
                                        onClickRowSimplified(event, row.original);
                                    }, onAuxClick: function (event) {
                                        onClickRowSimplified(event, row.original);
                                    } }, { children: row.getVisibleCells().map(function (cell, cellIdx) { return (_jsx("td", __assign({ className: "text-black-400 font-N8 px-[32px] py-[16px] ".concat(cellIdx == 0 ? "rounded-l-[10px]" : "", " ").concat(cellIdx == columnDefinitions.length - 1
                                            ? "rounded-r-[10px]"
                                            : "", " ").concat(cellIdx == columnDefinitions.length - 2
                                            ? "w-full"
                                            : "whitespace-nowrap"), style: { width: columnDefinitions[cellIdx].size } }, { children: flexRender(cell.column.columnDef.cell, cell.getContext()) }), cell.id)); }) }), row.id)); }) }), _jsx("tfoot", { children: table.getFooterGroups().map(function (footerGroup) { return (_jsx("tr", { children: footerGroup.headers.map(function (header) { return (_jsx("th", { children: header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.footer, header.getContext()) }, header.id)); }) }, footerGroup.id)); }) })] }))] })), _jsx(PaginationComponent, { paginated: paginated !== null && paginated !== void 0 ? paginated : false, table: table, maxFooterPages: maxFooterPages })] }));
};
export var PaginatedTable = function (props) {
    var baseUrl = props.baseUrl, itemsPerPage = props.itemsPerPage, rowsFromResponse = props.rowsFromResponse, rowCountFromResponse = props.rowCountFromResponse, columnDefinitions = props.columnDefinitions, onClickRow = props.onClickRow, canClickRow = props.canClickRow, buildRowLink = props.buildRowLink, refreshInterval = props.refreshInterval;
    var _a = React.useState({
        pageIndex: 0,
        pageSize: itemsPerPage,
    }), _b = _a[0], pageIndex = _b.pageIndex, pageSize = _b.pageSize, setPagination = _a[1];
    var _c = useAuth0(), isAuthenticated = _c.isAuthenticated, isLoading = _c.isLoading, getAccessTokenSilently = _c.getAccessTokenSilently;
    var _d = useSWR(isLoading || !isAuthenticated
        ? null
        : [
            "".concat(baseUrl).concat(baseUrl.includes("?") ? "&" : "?", "take=").concat(pageSize, "&skip=").concat(pageSize * pageIndex),
            getAccessTokenSilently,
        ], Auth0Fetcher, refreshInterval !== undefined
        ? { refreshInterval: refreshInterval }
        : undefined), data = _d.data, dataLoading = _d.isLoading;
    var columns = React.useMemo(function () { return columnDefinitions; }, [columnDefinitions]);
    var pagination = React.useMemo(function () { return ({
        pageIndex: pageIndex,
        pageSize: pageSize,
    }); }, [pageIndex, pageSize]);
    var pageCount = data ? rowCountFromResponse(data) : -1;
    if (pageCount > 0 && pageIndex >= pageCount) {
        setPagination({
            pageIndex: 0,
            pageSize: itemsPerPage,
        });
    }
    var table = useReactTable({
        data: data ? rowsFromResponse(data) : [],
        columns: columns,
        pageCount: pageCount,
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        onPaginationChange: setPagination,
        state: {
            pagination: pagination,
        },
    });
    return (_jsx(TableBase, { table: table, onClickRow: onClickRow, buildRowLink: buildRowLink, canClickRow: canClickRow, paginated: true, loading: dataLoading, columnDefinitions: columnDefinitions }));
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export var StaticTable = function (props) {
    var data = props.data, columnDefinitions = props.columnDefinitions, onClickRow = props.onClickRow, canClickRow = props.canClickRow, buildRowLink = props.buildRowLink, initialSorting = props.initialSorting, loading = props.loading, emptyMessage = props.emptyMessage;
    var columns = React.useMemo(function () { return columnDefinitions; }, [columnDefinitions]);
    var initialState = useMemo(function () {
        if (!initialSorting)
            return undefined;
        return {
            sorting: initialSorting,
        };
    }, [initialSorting]);
    var table = useReactTable({
        data: data ? data : [],
        columns: columns,
        pageCount: -1,
        initialState: initialState,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    return (_jsx(TableBase, { table: table, onClickRow: onClickRow, canClickRow: canClickRow, buildRowLink: buildRowLink, loading: loading, columnDefinitions: columnDefinitions, emptyMessage: emptyMessage }));
};
var PaginationComponent = function (_a) {
    var paginated = _a.paginated, table = _a.table, maxFooterPages = _a.maxFooterPages;
    var _b = useState(0), currentPage = _b[0], setCurrentPage = _b[1];
    var handlePageClick = function (pageIndex) {
        setCurrentPage(pageIndex);
        table.setPageIndex(pageIndex);
    };
    if (!paginated)
        return null;
    var totalPageCount = table.getPageCount();
    var basePage = Math.max(0, currentPage - Math.floor(maxFooterPages / 2));
    var maxPage = Math.min(totalPageCount, basePage + maxFooterPages);
    var pages = [];
    var _loop_1 = function (i) {
        pages.push(_jsx("label", __assign({ className: "font-N8 text-black-400 hover:text-white cursor-pointer ".concat(currentPage === i ? "text-white" : ""), onClick: function () { return handlePageClick(i); } }, { children: i + 1 }), i));
    };
    for (var i = basePage; i < maxPage; i++) {
        _loop_1(i);
    }
    return (_jsxs("div", __assign({ className: "mt-[16px] flex flex-row gap-x-[32px]" }, { children: [_jsx("label", __assign({ className: "font-N8 text-black-400 hover:text-white cursor-pointer", onClick: function () { return handlePageClick(0); } }, { children: "Page" })), pages, totalPageCount > maxFooterPages && maxPage != totalPageCount && (_jsxs(_Fragment, { children: [_jsx("label", __assign({ className: "font-N8 text-black-400" }, { children: "..." })), _jsx("label", __assign({ className: "font-N8 text-black-400 hover:text-white cursor-pointer ".concat(currentPage === totalPageCount - 1 ? "text-white" : ""), onClick: function () { return handlePageClick(totalPageCount - 1); } }, { children: totalPageCount }))] }))] })));
};
